/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable unicorn/no-nested-ternary */
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { getCleverTapInstance } from '@/config/clevertap';
import Constants from '@/Constants';
import logger from '@/logger';
import useStore from '@/store';

export type CleverTapEvent = {
  name: string;
  properties?: Record<string, string | number | boolean | null | undefined>;
};

type AdminDetails = {
  programCode?: string;
  centerCode?: string;
};

const useCleverTap = () => {
  const { currentStudent } = useStore();

  const clevertap = useMemo(() => getCleverTapInstance(), []);
  const lastIdentifiedStudentId = useRef<string | null>(null);

  useEffect(() => {
    if (!clevertap) return;

    // If no student, mark as initialized
    if (!currentStudent?.id) {
      return;
    }

    const studentId = currentStudent.id;

    if (lastIdentifiedStudentId.current === studentId) return;

    const adminDetails = JSON.parse(localStorage.getItem('adminDetails') || '{}') as AdminDetails;
    const homeAuthType = localStorage.getItem('homeAuthType');
    const accessLocation = localStorage.getItem('accessLocation');
    const loginLocation =
      accessLocation === 'home'
        ? homeAuthType === 'HOME_ACCESS_CODE'
          ? 'HOME_ACCESS_CODE'
          : 'HOME_MOBILE'
        : 'SCHOOL';

    try {
      clevertap.onUserLogin.push({
        Site: {
          Identity: studentId,
          grade: currentStudent.grade || '',
          program_code: adminDetails.programCode ?? '',
          center_code: adminDetails.centerCode ?? '',
          web_app_version: Constants.APP_VERSION,
          login_location: loginLocation,
        },
      });

      lastIdentifiedStudentId.current = studentId;

      logger.info(`CleverTap: Student ${studentId} identified successfully.`);
    } catch (error) {
      logger.error('CleverTap: Identification failed', error);
    }
  }, [clevertap, currentStudent]);

  const setUserProperties = useCallback(
    (properties: Record<string, string | number | boolean>) => {
      if (!clevertap) {
        logger.warn('CleverTap not initialized. Properties not set');
        return;
      }

      try {
        clevertap.profile.push({
          Site: properties,
        });
      } catch (error) {
        logger.error('CleverTap: Failed to set user properties', error);
      }
    },
    [clevertap],
  );

  return {
    setUserProperties,
  };
};

export default useCleverTap;
